/**
 * Typed API client for the Aurora server.
 *
 * Wraps fetch, attaches the JWT bearer token, unwraps the server's
 * ApiSuccess/ApiError envelope, and exposes a small typed surface for the pages.
 * All paths are same-origin relative (`/api/...`); the Vite dev proxy and the
 * production Nginx block both forward these to the Express server.
 */
import type {
  AssistantConversation,
  AssistantConversationDetail,
  AssistantRequest,
  AssistantRequestStatus,
  AssistantSendInput,
  AssistantStatus,
  AssistantUsageRow,
  AddSupplierInput,
  AddSupplierResult,
  BulkAddSuppliersResult,
  ApiResponse,
  BenchmarkAggregate,
  BenchmarkCitation,
  BenchmarkFeedback,
  BenchmarkInterpretation,
  BenchmarkPrompt,
  BenchmarkResult,
  BenchmarkRun,
  Client,
  CreateBenchmarkFeedbackInput,
  CreateBenchmarkInput,
  ManualSupplierEntry,
  CoveragePayload,
  ReportQcPayload,
  PromptQcRule,
  PromptRejectReason,
  PromptReviewPayload,
  ReportLayoutConfig,
  UpdateReportLayoutInput,
  FeedbackAction,
  Feedback,
  FeedbackType,
  CreateSelectionAuditInput,
  ContentAudit,
  ContentAuditPage,
  ContentAuditReport,
  CreateContentAuditInput,
  Lead,
  LeadBatch,
  LeadIntelligenceProfile,
  Paginated,
  ProductCategory,
  Recommendation,
  SelectionAudit,
  SelectionAuditEvidence,
  SelectionAuditReport,
  SupplierDictionaryEntry,
  SupplierMergeCandidate,
  ResolveSupplierMergeInput,
  ResolveSupplierMergeResult,
  SupplierMergeScanResult,
  SourceLookupResult,
  SourceCorrection,
  CorrectSourceCategoryInput,
  CorrectContentTypeInput,
  User,
} from '@aurora/shared';

/** A raw benchmark response as returned by GET /api/benchmarks/:id/results. */
export interface BenchmarkResponseSlim {
  id: string;
  runId: string;
  promptId: string;
  model: string;
  runIndex: number;
  responseText: string | null;
  citations: BenchmarkCitation[];
  conditions: Record<string, unknown>;
  retrievedAt: string | null;
}

/** Payload of GET /api/benchmarks/:id/results. */
export interface BenchmarkResultsPayload {
  run: BenchmarkRun;
  prompts: BenchmarkPrompt[];
  results: BenchmarkResult[];
  responses: BenchmarkResponseSlim[];
}

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const TOKEN_KEY = 'aurora.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/** A typed error carrying the server's machine code + HTTP status. */
export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** When set, send as multipart/form-data (no JSON content-type). */
  formData?: FormData;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body,
  });

  // Some endpoints (CSV export) are not JSON; callers use rawRequest for those.
  const json = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (json === null) {
    throw new ApiClientError(res.status, 'bad_response', 'Malformed response');
  }
  if (!json.ok) {
    throw new ApiClientError(
      res.status,
      json.error.code,
      json.error.message,
      json.error.details,
    );
  }
  return json.data;
}

/** Fetch a raw (non-JSON) response with auth — used for CSV download. */
async function rawRequest(path: string): Promise<Response> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_BASE}${path}`, { headers });
}

// ============================================================================
// Typed endpoint surface
// ============================================================================

export const api = {
  // --- Auth ---
  login: (email: string, password: string) =>
    request<{ token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  me: () => request<{ user: User }>('/api/auth/me'),

  // --- Leads ---
  listLeads: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)]),
    ).toString();
    return request<Paginated<Lead>>(`/api/leads${qs ? `?${qs}` : ''}`);
  },
  getLead: (id: string) => request<Lead>(`/api/leads/${id}`),
  getProfile: (id: string) =>
    request<LeadIntelligenceProfile>(`/api/leads/${id}/profile`),
  listBatches: () => request<Paginated<LeadBatch>>('/api/leads/batches'),
  reenrich: (id: string) =>
    request<{ leadId: string; status: string; enqueued: boolean }>(
      `/api/leads/${id}/reenrich`,
      { method: 'POST' },
    ),
  uploadCsv: (file: File, tags: string[]) => {
    const fd = new FormData();
    fd.append('file', file);
    if (tags.length) fd.append('tags', JSON.stringify(tags));
    return request<{
      batch: LeadBatch;
      created: number;
      enqueued: number;
      skipped: number;
      errors: Array<{ row: number; message: string }>;
    }>('/api/leads/upload', { method: 'POST', formData: fd });
  },

  // --- Recommendations ---
  getRecommendation: (leadId: string) =>
    request<{ recommendation: Recommendation | null }>(
      `/api/leads/${leadId}/recommendation`,
    ),
  regenerateRecommendation: (leadId: string) =>
    request<{ recommendation: Recommendation; warnings: string[] }>(
      `/api/leads/${leadId}/recommendation/regenerate`,
      { method: 'POST' },
    ),
  editRecommendation: (leadId: string, id: string, editedDraft: string) =>
    request<{ recommendation: Recommendation }>(
      `/api/leads/${leadId}/recommendation/${id}`,
      { method: 'PATCH', body: { editedDraft } },
    ),

  // --- Feedback ---
  submitFeedback: (
    leadId: string,
    type: FeedbackType,
    comment?: string,
    payload?: Record<string, unknown>,
  ) =>
    request<{ feedback: Feedback }>(`/api/leads/${leadId}/feedback`, {
      method: 'POST',
      body: { type, comment, payload },
    }),

  // --- Export ---
  exportLeadCsvUrl: (leadId: string) => `/api/leads/${leadId}/export.csv`,
  downloadLeadCsv: (leadId: string) =>
    rawRequest(`/api/leads/${leadId}/export.csv`),

  // --- AI Buyer Journey Benchmarks ---
  createBenchmark: (input: CreateBenchmarkInput) =>
    request<{ run: BenchmarkRun; enqueued: boolean }>('/api/benchmarks', {
      method: 'POST',
      body: input,
    }),
  /** library: 'current' = PL-2 onward, 'archive' = the PL-1 baseline, omitted = all. */
  listBenchmarks: (library?: 'current' | 'archive') =>
    request<Paginated<BenchmarkRun>>(
      library ? `/api/benchmarks?library=${library}` : '/api/benchmarks',
    ),
  getBenchmark: (id: string) =>
    request<{
      run: BenchmarkRun;
      aggregate: BenchmarkAggregate | null;
      interpretation: BenchmarkInterpretation | null;
    }>(`/api/benchmarks/${id}`),
  /** Permanently delete a run (throwaway/demo runs). Cascades to its children. */
  deleteBenchmark: (id: string) =>
    request<{ deleted: boolean; id: string }>(`/api/benchmarks/${id}`, {
      method: 'DELETE',
    }),
  // --- Source Intelligence citation coverage (7544ae15) ---
  getCoverage: (id: string) =>
    request<CoveragePayload>(`/api/benchmarks/${id}/coverage`),
  reviewCoverage: (
    id: string,
    body: { decision: 'accepted' | 'rejected'; reason: string },
  ) =>
    request<CoveragePayload>(`/api/benchmarks/${id}/coverage-review`, {
      method: 'POST',
      body,
    }),
  // --- Report QC (2026-10-22): the check before a client PDF ---
  getReportQc: (id: string) =>
    request<ReportQcPayload>(`/api/benchmarks/${id}/report-qc`),
  runReportQc: (id: string) =>
    request<ReportQcPayload>(`/api/benchmarks/${id}/report-qc/run`, { method: 'POST' }),
  reviewReportQc: (
    id: string,
    body: { decision: 'accepted' | 'rejected'; reason: string },
  ) =>
    request<ReportQcPayload>(`/api/benchmarks/${id}/report-qc/review`, {
      method: 'POST',
      body,
    }),
  refreshReport: (id: string) =>
    request<ReportQcPayload>(`/api/benchmarks/${id}/report/refresh`, { method: 'POST' }),
  // --- PL-2 question review (draft → approve) ---
  getPromptReview: (id: string) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/prompts`),
  editPrompt: (
    id: string,
    pid: string,
    patch: {
      prompt?: string;
      stage?: BenchmarkPrompt['stage'];
      productCategory?: string | null;
      application?: string | null;
      persona?: string | null;
    },
  ) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/prompts/${pid}`, {
      method: 'PATCH',
      body: patch,
    }),
  addPrompt: (
    id: string,
    input: { stage: BenchmarkPrompt['stage']; prompt?: string; instruction?: string },
  ) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/prompts`, {
      method: 'POST',
      body: input,
    }),
  rejectPrompt: (
    id: string,
    pid: string,
    reason: { reasonCode: PromptRejectReason; note?: string },
  ) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/prompts/${pid}/reject`, {
      method: 'POST',
      body: reason,
    }),
  regeneratePrompt: (id: string, pid: string, instruction?: string) =>
    request<PromptReviewPayload>(
      `/api/benchmarks/${id}/prompts/${pid}/regenerate`,
      { method: 'POST', body: instruction ? { instruction } : {} },
    ),
  resolvePromptFinding: (
    id: string,
    pid: string,
    input: { rule: PromptQcRule; kind: 'accepted' | 'exception' | 'clear'; reason?: string },
  ) =>
    request<PromptReviewPayload>(
      `/api/benchmarks/${id}/prompts/${pid}/resolve`,
      { method: 'POST', body: input },
    ),
  resolveLibraryFinding: (
    id: string,
    input: {
      rule: PromptQcRule;
      kind: 'accepted' | 'exception' | 'clear';
      reason?: string;
      explanation: string;
    },
  ) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/qc/resolve`, {
      method: 'POST',
      body: input,
    }),
  updatePromptScope: (
    id: string,
    scope: {
      productCategories: string[];
      applications: string[];
      personas: string[];
      scopeExclusions: string[];
      purpose?: 'client_benchmark' | 'source_intelligence';
      datasetId?: string;
      datasetMarket?: string;
      promptArchitecture?: 'PL-1' | 'PL-2';
    },
  ) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/scope`, {
      method: 'PATCH',
      body: scope,
    }),
  rerunPromptQc: (id: string) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/qc`, {
      method: 'POST',
      body: {},
    }),
  /** Replace the draft's questions with Aurora's own, pasted (PL-1 runs). */
  importPrompts: (id: string, text: string) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/prompts/import`, {
      method: 'POST',
      body: { text },
    }),
  regenerateAllPrompts: (id: string) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/regenerate-prompts`, {
      method: 'POST',
      body: {},
    }),
  approvePromptLibrary: (id: string) =>
    request<PromptReviewPayload & { enqueued: boolean }>(
      `/api/benchmarks/${id}/approve`,
      { method: 'POST', body: {} },
    ),
  revisePromptLibrary: (id: string) =>
    request<PromptReviewPayload>(`/api/benchmarks/${id}/revise`, {
      method: 'POST',
      body: {},
    }),
  getBenchmarkResults: (id: string) =>
    request<BenchmarkResultsPayload>(`/api/benchmarks/${id}/results`),
  downloadBenchmarkCsv: (id: string) =>
    rawRequest(`/api/benchmarks/${id}/export.csv`),
  downloadBenchmarkSourcesCsv: (id: string) =>
    rawRequest(`/api/benchmarks/${id}/sources.csv`),
  // The reviewed source workbook: the same Sources rows as the .csv plus a
  // Prompts tab (Joan, 2026-08-22). The .csv endpoint stays for scripts.
  downloadBenchmarkSourcesXlsx: (id: string) =>
    rawRequest(`/api/benchmarks/${id}/sources.xlsx`),
  /** The whole report as plain Markdown, for editing or pasting elsewhere. */
  downloadBenchmarkReportMd: (id: string) =>
    rawRequest(`/api/benchmarks/${id}/report.md`),
  /** The branded, print-ready HTML deliverable (offline-safe fallback). */
  downloadBenchmarkReportHtml: (id: string) =>
    rawRequest(`/api/benchmarks/${id}/report.html`),
  /** The branded report printed to PDF on the server — the client deliverable. */
  downloadBenchmarkReportPdf: (id: string) =>
    rawRequest(`/api/benchmarks/${id}/report.pdf`),
  /** A staff copy of a report that has not passed QC, stamped DRAFT. */
  downloadBenchmarkReportDraftPdf: (id: string) =>
    rawRequest(`/api/benchmarks/${id}/report.pdf?draft=1`),
  regenerateInterpretation: (id: string, guidance?: string) =>
    request<{ interpretation: BenchmarkInterpretation }>(
      `/api/benchmarks/${id}/interpretation/regenerate`,
      { method: 'POST', body: guidance ? { guidance } : {} },
    ),
  // Report layout config (per-client; applies to ALL of this client's reports).
  getBenchmarkLayout: () =>
    request<{ layout: ReportLayoutConfig }>('/api/benchmarks/layout'),
  updateBenchmarkLayout: (input: UpdateReportLayoutInput) =>
    request<{ layout: ReportLayoutConfig }>('/api/benchmarks/layout', {
      method: 'PUT',
      body: input,
    }),
  listBenchmarkFeedback: (id: string) =>
    request<{ feedback: BenchmarkFeedback[] }>(
      `/api/benchmarks/${id}/feedback`,
    ),
  createBenchmarkFeedback: (id: string, input: CreateBenchmarkFeedbackInput) =>
    request<{ feedback: BenchmarkFeedback }>(`/api/benchmarks/${id}/feedback`, {
      method: 'POST',
      body: input,
    }),
  applyBenchmarkFeedback: (
    id: string,
    fid: string,
    actions?: FeedbackAction[],
  ) =>
    request<{
      feedback: BenchmarkFeedback;
      applied: FeedbackAction[];
      skipped: FeedbackAction[];
      warnings: string[];
      aggregate: BenchmarkAggregate | null;
      interpretation: BenchmarkInterpretation | null;
    }>(`/api/benchmarks/${id}/feedback/${fid}/apply`, {
      method: 'POST',
      body: actions ? { actions } : {},
    }),
  dismissBenchmarkFeedback: (id: string, fid: string) =>
    request<{ feedback: BenchmarkFeedback }>(
      `/api/benchmarks/${id}/feedback/${fid}/dismiss`,
      { method: 'POST' },
    ),
  // Supplier dictionary (global/cross-client) — Joan's "add a supplier" tool.
  addSupplier: (input: AddSupplierInput) =>
    request<AddSupplierResult>('/api/benchmarks/suppliers', {
      method: 'POST',
      body: input,
    }),
  listManualSuppliers: () =>
    request<{ suppliers: ManualSupplierEntry[] }>(
      '/api/benchmarks/suppliers/manual',
    ),
  // The FULL supplier dictionary (dashboard view + CSV download).
  listSuppliers: () =>
    request<{ suppliers: SupplierDictionaryEntry[]; total: number }>(
      '/api/benchmarks/suppliers',
    ),
  downloadSuppliersCsv: () => rawRequest('/api/benchmarks/suppliers.csv'),
  importSuppliers: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<BulkAddSuppliersResult>('/api/benchmarks/suppliers/import', {
      method: 'POST',
      formData: fd,
    });
  },
  // Dictionary hygiene — suspected duplicate pairs awaiting human review.
  listSupplierMerges: () =>
    request<{ merges: SupplierMergeCandidate[] }>(
      '/api/benchmarks/suppliers/merges',
    ),
  resolveSupplierMerge: (id: string, input: ResolveSupplierMergeInput) =>
    request<ResolveSupplierMergeResult>(
      `/api/benchmarks/suppliers/merges/${id}/resolve`,
      { method: 'POST', body: input },
    ),
  scanSupplierMerges: () =>
    request<SupplierMergeScanResult>('/api/benchmarks/suppliers/merges/scan', {
      method: 'POST',
    }),

  // --- Sources page: look up a cited source and correct its category ---
  lookupSource: (q: string) =>
    request<SourceLookupResult>(
      `/api/benchmarks/sources/lookup?q=${encodeURIComponent(q)}`,
    ),
  listSourceCorrections: () =>
    request<{ corrections: SourceCorrection[] }>(
      '/api/benchmarks/sources/corrections',
    ),
  correctSourceCategory: (input: CorrectSourceCategoryInput) =>
    request<{ correction: SourceCorrection }>('/api/benchmarks/sources/category', {
      method: 'POST',
      body: input,
    }),
  correctContentType: (input: CorrectContentTypeInput) =>
    request<{ correction: SourceCorrection }>(
      '/api/benchmarks/sources/content-type',
      { method: 'POST', body: input },
    ),
  undoSourceCorrection: (id: string) =>
    request<{ correction: SourceCorrection }>(
      `/api/benchmarks/sources/corrections/${id}/undo`,
      { method: 'POST' },
    ),

  // --- AI Selection Readiness Audits (Audit 3) ---
  createSelectionAudit: (input: CreateSelectionAuditInput) =>
    request<{ run: SelectionAudit; enqueued: boolean }>(
      '/api/selection-audits',
      { method: 'POST', body: input },
    ),
  listSelectionAudits: () =>
    request<Paginated<SelectionAudit>>('/api/selection-audits'),
  getSelectionAudit: (id: string) =>
    request<{ run: SelectionAudit; report: SelectionAuditReport | null }>(
      `/api/selection-audits/${id}`,
    ),
  getSelectionAuditEvidence: (id: string) =>
    request<{ items: SelectionAuditEvidence[] }>(
      `/api/selection-audits/${id}/evidence`,
    ),
  downloadSelectionAuditMarkdown: (id: string) =>
    rawRequest(`/api/selection-audits/${id}/export.md`),

  // --- AI Content Discoverability Audits (Audit 1) ---
  createContentAudit: (input: CreateContentAuditInput) =>
    request<{ run: ContentAudit; enqueued: boolean }>('/api/content-audits', {
      method: 'POST',
      body: input,
    }),
  listContentAudits: () =>
    request<Paginated<ContentAudit>>('/api/content-audits'),
  getContentAudit: (id: string) =>
    request<{ run: ContentAudit; report: ContentAuditReport | null }>(
      `/api/content-audits/${id}`,
    ),
  getContentAuditPages: (id: string) =>
    request<{ items: ContentAuditPage[] }>(`/api/content-audits/${id}/pages`),
  downloadContentAuditMarkdown: (id: string) =>
    rawRequest(`/api/content-audits/${id}/export.md`),
  // The formatted client deliverable — branded, set in Poppins, print-to-PDF.
  downloadContentAuditHtml: (id: string) =>
    rawRequest(`/api/content-audits/${id}/export.html`),
  // The same document printed to PDF on the server.
  downloadContentAuditPdf: (id: string) =>
    rawRequest(`/api/content-audits/${id}/export.pdf`),

  // --- Admin ---
  listUsers: () => request<{ users: User[] }>('/api/admin/users'),
  createUser: (input: {
    email: string;
    password: string;
    fullName: string;
    role: 'admin' | 'manager' | 'rep';
  }) =>
    request<{ user: User }>('/api/admin/users', {
      method: 'POST',
      body: input,
    }),
  updateProductCategories: (productCategories: ProductCategory[]) =>
    request<{ client: Client }>('/api/admin/product-categories', {
      method: 'PUT',
      body: { productCategories },
    }),
  listApiKeys: () =>
    request<{ apiKeys: Array<{ id: string; name: string; prefix: string }> }>(
      '/api/admin/api-keys',
    ),
  createApiKey: (name: string) =>
    request<{ apiKey: { id: string; prefix: string }; rawKey: string }>(
      '/api/admin/api-keys',
      { method: 'POST', body: { name } },
    ),

  // --- Ask BLIP assistant ---
  assistant: {
    status: () =>
      request<AssistantStatus & { sqlReason?: string | null; mailConfigured?: boolean }>(
        '/api/assistant/status',
      ),
    list: (all = false) =>
      request<{ items: AssistantConversation[] }>(
        `/api/assistant/conversations${all ? '?all=1' : ''}`,
      ),
    start: (input: AssistantSendInput) =>
      request<{ conversationId: string; turnId: string }>('/api/assistant/conversations', {
        method: 'POST',
        body: input,
      }),
    send: (conversationId: string, input: AssistantSendInput) =>
      request<{ conversationId: string; turnId: string }>(
        `/api/assistant/conversations/${conversationId}/messages`,
        { method: 'POST', body: input },
      ),
    get: (conversationId: string, after = 0) =>
      request<AssistantConversationDetail>(
        `/api/assistant/conversations/${conversationId}?after=${after}`,
      ),
    retry: (conversationId: string) =>
      request<{ conversationId: string; turnId: string }>(
        `/api/assistant/conversations/${conversationId}/retry`,
        { method: 'POST' },
      ),
    archive: (conversationId: string) =>
      request<{ archived: boolean }>(`/api/assistant/conversations/${conversationId}`, {
        method: 'DELETE',
      }),
    requests: () => request<{ items: AssistantRequest[] }>('/api/assistant/requests'),
    updateRequest: (id: string, status: AssistantRequestStatus, resolutionNote?: string) =>
      request<{ request: AssistantRequest }>(`/api/assistant/requests/${id}`, {
        method: 'PATCH',
        body: { status, ...(resolutionNote ? { resolutionNote } : {}) },
      }),
    usage: () => request<{ items: AssistantUsageRow[] }>('/api/assistant/usage'),
    /** Any same-origin download the assistant links to (CSV / PDF / report). */
    download: (href: string) => rawRequest(href),
  },
};
